// src/pages/WardClassesPage.tsx
//
// ★ PHASE 6, TASK 18 — Annex A story 26: "Class of Wards Record Management".
//
// The ward-class CLASSIFICATION catalogue — code, label, category and subsidy
// rank — never a rate (`app.reference_value`'s `PEGGED_WARD_RATE` series,
// readable at `GET /api/reference/PEGGED_WARD_RATE`, is the one place a rate
// lives; see `028_ward_class.sql`'s own header for why).
//
// ⚠ PSD only, both read and write (`GET /api/administration/ward-classes`
// narrows to an empty page for anyone else). A non-PSD session sees the same
// empty-scope card `ContactsPage` shows a grant-less scope.
//
// ⚠ Export: PDF/Excel via the shared export kernel (P2C1 §12.52) — unlike
// `ContactsPage`/`RecordsPage`, which predate it and are print-only.
//
// ~~⚠ Not built on this screen: a history/point-in-time view of one code's past
// versions. `GET /api/administration/ward-classes/:code?asRecordedAt=` exists on the
// API; this screen shows only the CURRENT catalogue …~~
//
// ★★★ **WAVE 12 (P4) — BUILT.** Every row carries a **History** control, and the
// panel it opens renders the code's whole series oldest-first with a
// point-in-time rewind over it (`components/VersionHistory.tsx`). The route had
// answered this since Phase 6 with **no caller at all** — and an endpoint no client
// calls is indistinguishable from one that does not work, which is why the RTM
// carried it as a gap on Annex A 26–28 rather than as a nicety.
//
// ⚠ **Wave 11 is why this is honest today.** S-078 found that the bi-temporal bound
// this view depends on was inclusive against a microsecond column in TWELVE
// repositories, so asking for a version's own recorded instant returned an EMPTY
// history — 0 rows where 1 was correct, measured on the live estate. A screen built
// before that fix would have rendered a confidently wrong reconstruction of the past,
// over the catalogues pro-ration reads from.
import { CategoryBadge } from '../components/StatusBadge'
import { useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { FormResult } from '../components/FormResult'
import { BoundedField } from '../components/BoundedField'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { DatePicker } from '../components/DatePicker'
import { VersionHistory } from '../components/VersionHistory'
import { boundsProblems } from '../lib/published-bounds'
import { wholeNumberProblem } from '../components/FieldError'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import { plainProse } from '../lib/plain-words'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'

type WardClassSortKey = 'code' | 'label' | 'category' | 'rankOrder' | 'description' | 'effectiveFrom'

const COLUMN_LABELS: Readonly<Record<WardClassSortKey, string>> = {
  code: 'Code',
  label: 'Label',
  category: 'Category',
  rankOrder: 'Rank',
  description: 'Description',
  effectiveFrom: 'Effective from',
}

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of each bounded field's label: read by
 * `boundsProblems` for its messages AND by the field that renders it. Two copies
 * drift the day one of them is reworded.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here. A field the route
 * checks for presence alone is deliberately absent, and its screen therefore says
 * nothing beyond presence — see `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  code: 'Code',
  label: 'Label',
  category: 'Category',
  rankOrder: 'Rank order',
  note: 'Note (optional)',
} as const

/**
 * ★★ WAVE 8 (Task 3) REVIEW FINDING — the bounded fields that ONLY EXIST ON AN
 * ESTABLISH, and are therefore not part of a RETIRE's problem set.
 *
 * ⚠ Ticking "Retire this code instead of superseding it" unmounts these three and
 * drops them from the request body, but their React state lives on this page and
 * survives the unmount. Without naming them here, a `0` typed into Rank order
 * before the box was ticked kept `problems.any` true and **disabled Save with no
 * error visible anywhere** — `FieldError.tsx`'s header records that exact failure
 * from Wave 5. See `lib/published-bounds.ts`'s `BoundsProblemOptions`.
 *
 * ⚠ This list is the SUBMITTED half of `handleSubmit`'s own `!retireInstead &&`
 * spread below. If one changes, the other has to.
 */
const ESTABLISH_ONLY_FIELDS = ['label', 'category', 'rankOrder'] as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

export function WardClassesPage() {
  const { sort, onSort, interacted } = useSort<WardClassSortKey>()
  const wardClasses = useApi(() => api.wardClasses(sortParams(sort)), [sort.column, sort.dir])

  /**
   * ★★★ WAVE 12 (P4) — THE VERSION HISTORY, and the bi-temporal rewind over it.
   *
   * ⚠ `historyCode` is `''` until an officer asks for a code's history, and the
   * fetcher RESOLVES TO NULL rather than calling the route with an empty code. A
   * `GET .../ward-classes/` with no code is a different route (the list) and would
   * quietly return the whole catalogue into a component that renders it as one
   * code's history — the "answered something adjacent" failure this codebase refuses
   * elsewhere by name.
   */
  const [historyCode, setHistoryCode] = useState('')
  const [asRecordedAt, setAsRecordedAt] = useState('')
  const history = useApi(
    () => (historyCode.length === 0 ? Promise.resolve(null) : api.wardClassSeries(historyCode, asRecordedAt)),
    [historyCode, asRecordedAt],
  )

  const [code, setCode] = useState('')
  const [label, setLabel] = useState('')
  const [category, setCategory] = useState('')
  const [rankOrder, setRankOrder] = useState('')
  const [description, setDescription] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [retireInstead, setRetireInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })
  /**
   * ★★ WAVE 5 — the ONE type-level problem this screen can know before it asks.
   *
   * `docs/delivery/DEMO-READINESS-PLAN.md` names it: "`rankOrder` accepts `1.5`
   * and `1e999`". Both reach the route today and both are refused there — but
   * `1e999` becomes `Infinity`, and `JSON.stringify` turns that into `null`, so
   * the officer gets a message about a MISSING rank for a field they plainly
   * filled in. That is the confusion, and it is knowable here.
   *
   * ⚠ It checks the SHAPE and not the BOUND. `rankOrder >= 1` is
   * `routes/ward-classes.ts`'s rule and stays there — a second copy is the S-011
   * shape. See `FieldError`'s own header.
   *
   * ★★ WAVE 8 (Task 3) — and the bound is now REPORTED as well, without this file
   * holding it: the route publishes `validation.rankOrder.min` and
   * `problems.of.rankOrder` compares against that. The two are combined into
   * `rankOrderProblem` below, type problem first, because "1.5 is not a whole
   * number" is the more specific thing to say about `1.5`.
   */
  const rankOrderTypeProblem = wholeNumberProblem(rankOrder, BOUNDED_FIELDS.rankOrder)

  const rows = wardClasses.data?.wardClasses ?? []
  const total = wardClasses.data?.total ?? 0

  /*
    ⚠ The bounds come from the PAYLOAD (`validation`), never from this file, and
    they are checked against the value that will actually be SENT rather than the
    keystrokes in the box — see `lib/published-bounds.ts` for why both of those
    matter. `undefined` while the list is loading or refused, in which case the
    fields show nothing beyond `required` and the route refuses on its own.
  */
  const validation = wardClasses.data?.validation
  const problems = boundsProblems(
    validation,
    {
      code: code.trim().toUpperCase(),
      label: label.trim(),
      category: category.trim(),
      rankOrder: rankOrder.trim(),
      note: note.trim(),
    },
    BOUNDED_FIELDS,
    // ⚠ A RETIRE sends none of the establish-only fields, so none of them can be
    // in breach — see ESTABLISH_ONLY_FIELDS above for the disabled-with-no-message
    // defect this closes.
    { notSubmitted: retireInstead ? ESTABLISH_ONLY_FIELDS : [] },
  )

  /** The published floor (rankOrder >= 1) and the type check, in the order the officer meets them. */
  const rankOrderProblem = rankOrderTypeProblem ?? problems.of.rankOrder

  const canSubmit =
    code.trim().length > 0 &&
    effectiveFrom.length > 0 &&
    !problems.any &&
    // ⚠ `rankOrderProblem === null` too: a field the screen already knows is
    // malformed must not be sent, or the officer waits for a round trip to be
    // told what was on screen the whole time.
    (retireInstead ||
      (label.trim().length > 0 && category.trim().length > 0 && rankOrder.trim().length > 0 && rankOrderProblem === null))

  /**
   * ★★ WAVE 26 — Save FREEZES a sentence and asks; the write happens only from the
   * confirmation (`ConfirmWrite`'s header says why every irreversible write does
   * this now). The sentence is composed here, once, from the fields as they were
   * when Save was pressed — never re-derived from a form the officer may since
   * have edited.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const upper = code.trim().toUpperCase()
    setConfirming(
      retireInstead
        ? `Retire ward class ${upper} from ${effectiveFrom}. No successor window opens, so ward-scheme bills with a service date on or after that day cannot be classified against it. There is no undo. A mistake is corrected by another version.`
        : `Supersede ward class ${upper} from ${effectiveFrom}: "${label.trim()}", ${category.trim()}, rank ${rankOrder.trim()}. Ward-scheme bills with a service date on or after that day are classified against the new version. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeWardClass(code.trim().toUpperCase(), {
        effectiveFrom,
        establish: !retireInstead,
        ...(note.trim().length > 0 && { note: note.trim() }),
        ...(!retireInstead && {
          label: label.trim(),
          category: category.trim(),
          rankOrder: Number(rankOrder),
          description: description.trim().length > 0 ? description.trim() : null,
        }),
      })) as { outcome: string }
      setForm({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? `${code.trim().toUpperCase()} retired from ${effectiveFrom}.`
            : `${code.trim().toUpperCase()} superseded from ${effectiveFrom}.`,
      })
      setCode('')
      setLabel('')
      setCategory('')
      setRankOrder('')
      setDescription('')
      setEffectiveFrom('')
      setNote('')
      wardClasses.reload()
    } catch (error) {
      setForm({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not save this ward class.' })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">Class of wards</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            The ward classification catalogue: code, label, category and subsidy rank. No
            rate is held here: pegged ward rates are kept under Reference data.
            Effective-dated, PSD-only.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onDownload={(format) => {
              window.location.href = api.wardClassExportUrl(format)
            }}
            email={<EmailReportButton path="/api/administration/ward-classes/export" />}
          />
        </div>
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={wardClasses.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {wardClasses.error !== null ? (
        <ErrorCard title="Could not load ward classes" message={wardClasses.error} onRetry={wardClasses.reload} />
      ) : wardClasses.initialLoading ? (
        <LoadingCard label="Loading ward classes" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No ward classes in scope"
          message="Either nothing is currently in force, or your active identity is not PSD: this catalogue is administered by PSD only."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={wardClasses.loading}>
            <table className="pt-table">
              <caption className="sr-only">Ward classes in force, with code, label, category, subsidy rank and effective window</caption>
              <thead>
                <tr>
                  <SortableTh label="Code" sortKey="code" sort={sort} onSort={onSort} />
                  <SortableTh label="Label" sortKey="label" sort={sort} onSort={onSort} />
                  <SortableTh label="Category" sortKey="category" sort={sort} onSort={onSort} />
                  <SortableTh label="Rank" sortKey="rankOrder" sort={sort} onSort={onSort} />
                  <SortableTh label="Description" sortKey="description" sort={sort} onSort={onSort} />
                  <SortableTh label="Effective from" sortKey="effectiveFrom" sort={sort} onSort={onSort} />
                  {/* ⚠ Not sortable, and not data: the column holds one control per
                      row. A sortable header over buttons would claim to order
                      something that has no order. */}
                  <th scope="col" className="pt-no-print" data-unsortable="one control per row, not data — a sortable header over buttons would claim to order something with no order">
                    History
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-semibold pt-mono">{row.code}</td>
                    <td>{row.label}</td>
                    <td><CategoryBadge kind="wardCategory" value={row.category} /></td>
                    <td>{row.rankOrder}</td>
                    <td className="text-sm">{row.description === null ? 'None' : plainProse(row.description)}</td>
                    <td className="text-sm" title={formatInstant(row.recordedAt)}>
                      {formatIsoDate(row.effective.from)}
                    </td>
                    <td className="pt-no-print">
                      {/*
                        ★★★ WAVE 12 (P4) — the way IN to the history. The series route
                        has existed since Phase 6 with no caller; this is the control
                        that makes it reachable, per code, from the row it belongs to.

                        ⚠ Selecting a code RESETS the rewind. An instant chosen while
                        reading code A is not a statement about code B, and carrying it
                        across would silently reconstruct the new code at an instant
                        the officer had forgotten they set — the same "a pin belongs to
                        the walk, not to the screen" rule the audit trail follows.
                      */}
                      <SgdsButton
                        variant={historyCode === row.code ? 'primary' : 'ghost'}
                        size="sm"
                        type="button"
                        onClick={() => {
                          setAsRecordedAt('')
                          setHistoryCode(historyCode === row.code ? '' : row.code)
                        }}
                      >
                        {historyCode === row.code ? 'Hide history' : 'History'}
                      </SgdsButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            Showing {rows.length} of {total} currently in-force ward class{total === 1 ? '' : 'es'}.{' '}
            {sort.column === null ? 'The server default order.' : `Sorted by ${COLUMN_LABELS[sort.column]} (${sort.dir}).`}
          </p>
        </div>
      )}

      {/*
        ★★★ WAVE 12 (P4) — the history, rendered only when a code has been chosen.
        Placed directly under the catalogue it belongs to and above the write form,
        because an officer reads the past before they supersede.
      */}
      {historyCode.length > 0 && (
        <VersionHistory
          subject={historyCode}
          entries={history.data?.entries ?? []}
          loading={history.loading}
          error={history.error}
          onRetry={history.reload}
          asRecordedAt={asRecordedAt}
          onAsRecordedAtChange={setAsRecordedAt}
          columns={[
            { header: 'Label', cell: (entry) => entry.label },
            { header: 'Category', cell: (entry) => entry.category },
            { header: 'Rank', cell: (entry) => entry.rankOrder },
          ]}
        />
      )}

      <div className="pt-card flex flex-col gap-5 pt-no-print">
        <div>
          <h2 className="text-xl mb-1">Establish or retire a ward class</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)]">
            Superseding is the only way a ward class changes: there is no edit-in-place. The current window
            (if any) closes on the date you give, and either a new window opens with the values below, or (if
            "retire" is checked) nothing succeeds it.
          </p>
        </div>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <BoundedField
            label={BOUNDED_FIELDS.code}
            example="e.g. A, B1, B2, C."
            value={code}
            onValueChange={setCode}
            bounds={validation?.code}
            problem={problems.of.code}
            required
          />
          {/*
            ★ WAVE 26 — the house `DatePicker`, the one calendar in the console.
            This field (and fifteen like it on thirteen other screens) stayed a
            native date input until 2026-08-26 because the only house picker
            then said "service date" throughout, and an administrative
            effective-date is not a clinical one. The walk of the running product
            found the cost: the browser's own picker — `mm/dd/yyyy`, Sunday-first,
            unstyleable — beside the product's on the next screen. `DatePicker`
            takes a `subject`, so the words fit and the calendar is the same. The
            value is still the ISO `YYYY-MM-DD` string the API expects.
          */}
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold">
              Effective from <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} required />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={retireInstead} onChange={(e) => setRetireInstead(e.target.checked)} />
            Retire this code instead of superseding it (no successor window opens)
          </label>
          {!retireInstead && (
            <>
              <BoundedField
                label={BOUNDED_FIELDS.label}
                value={label}
                onValueChange={setLabel}
                bounds={validation?.label}
                problem={problems.of.label}
                required
              />
              <BoundedField
                label={BOUNDED_FIELDS.category}
                value={category}
                onValueChange={setCategory}
                bounds={validation?.category}
                problem={problems.of.category}
                required
              />
              {/*
                ★★ WAVE 8 (Task 3) — the rank-order floor now comes from the
                PAYLOAD. It used to be the one field on this screen with a
                hand-wired `FieldError`, and its own comment said the bound
                itself had to stay on the server: *"`rankOrder >= 1` is
                `routes/ward-classes.ts`'s rule and stays there — a second copy
                is the S-011 shape."* That is still true; the rule is simply
                PUBLISHED from there now, so the screen can state it without
                holding a copy.

                ⚠ Two problems, deliberately in this order: the TYPE problem
                (`1.5`, `1e999`) is knowable here and is reported first, then the
                published floor. `BoundedField` keeps the `type="text"` +
                `inputMode="numeric"` shape this field measured the need for — a
                `type="number"` input DISCARDS `1e999` before this app can read
                it, which `FieldError.tsx`'s header records at length.
              */}
              <BoundedField
                label={BOUNDED_FIELDS.rankOrder}
                example="1 = least subsidised."
                value={rankOrder}
                onValueChange={setRankOrder}
                bounds={validation?.rankOrder}
                problem={rankOrderProblem}
                inputMode="numeric"
                required
              />
              <SgdsInput placeholder="" label="Description (optional)" value={description} onSgdsInput={(e) => setDescription(targetValue(e))} />
            </>
          )}
          <BoundedField
            className="sm:col-span-2"
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
          />
          {confirming !== null && (
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          )}
          <FormResult state={form} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <SgdsButton type="submit" variant="primary" disabled={!canSubmit || confirming !== null} loading={form.status === 'submitting'}>
              {retireInstead ? 'Retire' : 'Save'}
            </SgdsButton>
          </div>
        </form>
      </div>
    </div>
  )
}
